import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { ChevronLeft, ChevronRight, Upload, Check, X, Camera, Eye, Snowflake, Sun, AlertTriangle, FileText, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FUEL_OPTIONS = ["Dízel", "Benzin", "Benzin/LPG", "Elektromos", "Hibrid", "Plug-in Hibrid"];
const TRANSMISSION_OPTIONS = ["Automata", "Manuális", "Félautomata"];
const DRIVE_OPTIONS = ["Elsőkerék", "Hátsókerék", "Összkerék (4x4)"];
const BODY_OPTIONS = ["Szedán", "Kombi", "SUV", "Kupé", "Kabrió", "Ferdehátú", "Egyterű"];
const SOURCE_OPTIONS = ["Magánszemély", "Kereskedő", "Lízing visszavétel"];
const SERVICE_BOOK_OPTIONS = ["Teljeskörű", "Hiányos", "Nincs"];
const USAGE_OPTIONS = ["Nincs", "Taxi", "Bérleti", "Oktatójármű"];
const COUNTRY_OPTIONS = ["Ausztria", "Magyarország", "Németország", "Szlovákia", "Csehország", "Olaszország", "Franciaország", "Svájc", "Egyéb"];

const PAINT_PANELS = [
  { key: "front_bumper", label: "Első lökhárító" },
  { key: "hood", label: "Motorháztető" },
  { key: "right_front_fender", label: "Jobb első sárvédő" },
  { key: "right_front_door", label: "Jobb első ajtó" },
  { key: "roof", label: "Tető" },
  { key: "right_rear_door", label: "Jobb hátsó ajtó" },
  { key: "right_rear_fender", label: "Jobb hátsó sárvédő" },
  { key: "trunk", label: "Csomagtér fedél" },
  { key: "rear_bumper", label: "Hátsó lökhárító" },
  { key: "left_front_fender", label: "Bal első sárvédő" },
  { key: "left_front_door", label: "Bal első ajtó" },
  { key: "left_rear_door", label: "Bal hátsó ajtó" },
  { key: "left_rear_fender", label: "Bal hátsó sárvédő" },
];

const IMAGE_CATEGORIES = [
  { id: "front", label: "Elölnézet", min: 1 },
  { id: "front_angle", label: "Elöl oldalról (bal/jobb)", min: 2 },
  { id: "side", label: "Oldalról (bal/jobb)", min: 2 },
  { id: "rear", label: "Hátulnézet", min: 1 },
  { id: "rear_angle", label: "Hátul oldalról", min: 1 },
  { id: "interior", label: "Belső tér", min: 3 },
  { id: "dashboard", label: "Műszerfal / Kijelző", min: 2 },
  { id: "engine", label: "Motor", min: 2 },
  { id: "trunk", label: "Csomagtér", min: 1 },
  { id: "wheels", label: "Kerekek / Fékek", min: 2 },
  { id: "details", label: "Részletek / Extra", min: 2 },
  { id: "damage", label: "Sérülések (ha van)", min: 0 },
];

const DOC_CATEGORIES = [
  { id: "invoices", label: "Számlák / Bizonylatok", min: 0 },
  { id: "registration_doc", label: "Forgalmi engedély", min: 0 },
];

const STEPS = [
  { id: "basic", label: "Alapadatok" },
  { id: "origin", label: "Származás" },
  { id: "technical", label: "Műszaki" },
  { id: "images", label: "Képek & Dok." },
  { id: "preview", label: "Előnézet" },
];

function FieldInput({ label, value, onChange, required = true, type = "text", placeholder = "", ...props }) {
  return (
    <div>
      <label className="block text-xs text-white/50 mb-1">{label} {required && <span className="text-red-400">*</span>}</label>
      <input type={type} value={value} onChange={onChange} required={required} placeholder={placeholder}
        className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none transition-colors"
        {...props} />
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, required = true, placeholder = "Válasszon..." }) {
  return (
    <div>
      <label className="block text-xs text-white/50 mb-1">{label} {required && <span className="text-red-400">*</span>}</label>
      <select value={value} onChange={onChange} required={required}
        className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none appearance-none">
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${checked ? "border-[#D4AF37]/40 bg-[#D4AF37]/10" : "border-white/10 bg-white/5"}`}>
      <div className={`w-9 h-5 rounded-full transition-colors relative ${checked ? "bg-[#D4AF37]" : "bg-white/20"}`}>
        <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`} />
      </div>
      <span className="text-sm text-white/80">{label}</span>
    </button>
  );
}

function TireSetFields({ label, icon: Icon, data, onChange }) {
  return (
    <div className="border border-white/5 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-[#D4AF37]" />
        <span className="text-sm font-medium text-white">{label}</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <FieldInput label="Márka" value={data.brand} onChange={e => onChange({ ...data, brand: e.target.value })} required={false} placeholder="pl. Michelin" />
        <FieldInput label="Méret" value={data.size} onChange={e => onChange({ ...data, size: e.target.value })} required={false} placeholder="pl. 225/45 R17" />
        <FieldInput label="Profilmélység (mm)" value={data.tread_depth} onChange={e => onChange({ ...data, tread_depth: e.target.value })} required={false} placeholder="pl. 5.5" />
      </div>
    </div>
  );
}

export default function SubmitCar() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);
  const [uploadCategory, setUploadCategory] = useState(null);

  const [submitter, setSubmitter] = useState({ name: "", email: "", phone: "" });

  const [car, setCar] = useState({
    brand: "", model: "", year: "", price: "", mileage: "", fuel: "", engine: "", power: "",
    transmission: "", drive: "", body_type: "", color: "", vin: "", equipment: "", description: "",
  });

  const [origin, setOrigin] = useState({
    location: "", source_type: "", imported: false, last_registration_country: "",
  });

  const [inspection, setInspection] = useState({
    technical_inspection_valid_until: "", last_service_km: "", service_book_status: "",
  });

  const [history, setHistory] = useState({
    first_registration: "", mileage: "", previous_owners: "", unusual_usage: "", key_count: "",
  });

  // Tires
  const [tires, setTires] = useState({
    summer: { enabled: false, brand: "", size: "", tread_depth: "" },
    winter: { enabled: false, brand: "", size: "", tread_depth: "" },
  });

  // Damage / Paint thickness
  const [damage, setDamage] = useState({
    has_damage: false,
    description: "",
    damaged_panels: [],
  });

  // Images
  const [imagesByCategory, setImagesByCategory] = useState({});
  const allCategories = [...IMAGE_CATEGORIES, ...DOC_CATEGORIES];
  const imageOnlyTotal = IMAGE_CATEGORIES.reduce((sum, cat) => sum + (imagesByCategory[cat.id] || []).length, 0);
  const allImages = Object.entries(imagesByCategory).flatMap(([, imgs]) => imgs);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!uploadCategory) return;
    const newFiles = files.map(f => ({ file: f, preview: URL.createObjectURL(f), category: uploadCategory }));
    setImagesByCategory(prev => ({
      ...prev,
      [uploadCategory]: [...(prev[uploadCategory] || []), ...newFiles]
    }));
    e.target.value = "";
  };

  const removeImage = (category, idx) => {
    setImagesByCategory(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== idx)
    }));
  };

  const toggleDamagedPanel = (key) => {
    setDamage(prev => ({
      ...prev,
      damaged_panels: prev.damaged_panels.includes(key)
        ? prev.damaged_panels.filter(k => k !== key)
        : [...prev.damaged_panels, key]
    }));
  };

  const generatePaintThickness = () => {
    const result = {};
    PAINT_PANELS.forEach(({ key }) => {
      if (damage.has_damage && damage.damaged_panels.includes(key)) {
        result[key] = String(Math.floor(Math.random() * 140) + 160);
      } else {
        const base = 90 + Math.floor(Math.random() * 55);
        result[key] = String(base + Math.floor(Math.random() * 6));
      }
    });
    return result;
  };

  const buildTireInspection = () => {
    const result = {};
    const buildSet = (label, data) => ({
      label,
      front_left: { type: `${data.brand} ${data.size}`.trim(), tread_depth: data.tread_depth },
      front_right: { type: `${data.brand} ${data.size}`.trim(), tread_depth: data.tread_depth },
      rear_left: { type: `${data.brand} ${data.size}`.trim(), tread_depth: data.tread_depth },
      rear_right: { type: `${data.brand} ${data.size}`.trim(), tread_depth: data.tread_depth },
    });
    if (tires.summer.enabled && (tires.summer.brand || tires.summer.size || tires.summer.tread_depth)) {
      result.primary = buildSet("Nyári gumik", tires.summer);
    }
    if (tires.winter.enabled && (tires.winter.brand || tires.winter.size || tires.winter.tread_depth)) {
      const key = result.primary ? "secondary" : "primary";
      result[key] = buildSet("Téli gumik", tires.winter);
    }
    return result;
  };

  const validateStep = (s) => {
    const errs = [];
    if (s === 0) {
      if (!submitter.name) errs.push("Név megadása kötelező");
      if (!submitter.email) errs.push("Email megadása kötelező");
      if (!submitter.phone) errs.push("Telefonszám megadása kötelező");
      ["brand", "model", "year", "price", "mileage", "fuel", "engine", "power", "transmission", "drive", "body_type", "color", "vin"].forEach(f => {
        if (!car[f]) errs.push(`${f} megadása kötelező`);
      });
    }
    if (s === 1) {
      ["location", "source_type", "last_registration_country"].forEach(f => {
        if (!origin[f]) errs.push(`Származás: ${f} megadása kötelező`);
      });
      ["technical_inspection_valid_until", "last_service_km", "service_book_status"].forEach(f => {
        if (!inspection[f]) errs.push(`Műszaki vizsgálat: ${f} megadása kötelező`);
      });
    }
    if (s === 2) {
      ["first_registration", "previous_owners", "key_count"].forEach(f => {
        if (!history[f]) errs.push(`Történet: ${f} megadása kötelező`);
      });
    }
    if (s === 3) {
      if (imageOnlyTotal < 20) errs.push(`Minimum 20 kép szükséges (jelenleg: ${imageOnlyTotal})`);
      IMAGE_CATEGORIES.forEach(cat => {
        const count = (imagesByCategory[cat.id] || []).length;
        if (count < cat.min) errs.push(`"${cat.label}": minimum ${cat.min} kép szükséges (jelenleg: ${count})`);
      });
    }
    return errs;
  };

  const nextStep = () => {
    if (step === 4) return;
    const errs = validateStep(step);
    if (errs.length > 0) { setErrors(errs); return; }
    setErrors([]);
    setStep(s => s + 1);
  };

  const prevStep = () => { setErrors([]); setStep(s => Math.max(0, s - 1)); };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        submitter_name: submitter.name,
        submitter_email: submitter.email,
        submitter_phone: submitter.phone,
        ...car,
        vehicle_origin: origin,
        vehicle_inspection: inspection,
        vehicle_history: { ...history, mileage: car.mileage },
        paint_thickness: generatePaintThickness(),
        tire_inspection: buildTireInspection(),
        damage_description: damage.has_damage ? damage.description : "",
      };
      const res = await axios.post(`${BACKEND_URL}/api/cars/submit`, payload);
      const carId = res.data.id;

      const files = allImages.map(i => i.file);
      for (let i = 0; i < files.length; i += 5) {
        const batch = files.slice(i, i + 5);
        const fd = new FormData();
        batch.forEach(f => fd.append("files", f));
        await axios.post(`${BACKEND_URL}/api/cars/${carId}/upload-images`, fd);
      }

      toast.success("Hirdetés sikeresen beküldve! Ellenőrzés után jelenik meg.");
      setTimeout(() => navigate("/"), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba történt a beküldés során");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Header */}
      <div className="border-b border-white/5 px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/alpine-car-logo.png" alt="Alpine Car" className="w-10 h-10 rounded-lg object-cover" />
            <span className="font-bold text-lg">Alpine Car</span>
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-8 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Autó hirdetés feladása</h1>
          <p className="text-white/40 text-sm">Adja meg autója részletes adatait. Admin jóváhagyás után jelenik meg.</p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-1 mb-8" data-testid="step-progress">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center flex-1">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                i < step ? "bg-green-500/10 text-green-400 border border-green-500/20"
                : i === step ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20"
                : "bg-white/5 text-white/30 border border-white/5"
              }`}>
                {i < step ? <Check className="w-3 h-3" /> : <span>{i + 1}</span>}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-1 ${i < step ? "bg-green-500/30" : "bg-white/5"}`} />}
            </div>
          ))}
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6" data-testid="validation-errors">
            <p className="text-red-400 text-sm font-medium mb-2">Hiányzó mezők:</p>
            {errors.map((e, i) => <p key={i} className="text-red-300/70 text-xs">- {e}</p>)}
          </div>
        )}

        {/* Step 0: Basic Info */}
        {step === 0 && (
          <div className="space-y-6" data-testid="step-basic">
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-sm mb-4 text-[#D4AF37]">Az Ön adatai</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <FieldInput label="Teljes név" value={submitter.name} onChange={e => setSubmitter(p => ({ ...p, name: e.target.value }))} placeholder="Kovács János" data-testid="submitter-name" />
                <FieldInput label="Email" type="email" value={submitter.email} onChange={e => setSubmitter(p => ({ ...p, email: e.target.value }))} placeholder="email@pelda.hu" data-testid="submitter-email" />
                <FieldInput label="Telefon" type="tel" value={submitter.phone} onChange={e => setSubmitter(p => ({ ...p, phone: e.target.value }))} placeholder="+36 30 123 4567" data-testid="submitter-phone" />
              </div>
            </div>

            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-sm mb-4 text-[#D4AF37]">Jármű alapadatok</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <FieldInput label="Márka" value={car.brand} onChange={e => setCar(p => ({ ...p, brand: e.target.value }))} placeholder="pl. BMW" />
                <FieldInput label="Modell" value={car.model} onChange={e => setCar(p => ({ ...p, model: e.target.value }))} placeholder="pl. 530d" />
                <FieldInput label="Évjárat" value={car.year} onChange={e => setCar(p => ({ ...p, year: e.target.value }))} placeholder="pl. 2019" />
                <FieldInput label="Ár (€)" type="number" value={car.price} onChange={e => setCar(p => ({ ...p, price: e.target.value }))} placeholder="pl. 15000" />
                <FieldInput label="Kilométeróra állás" value={car.mileage} onChange={e => setCar(p => ({ ...p, mileage: e.target.value }))} placeholder="pl. 150 000 km" />
                <FieldSelect label="Üzemanyag" value={car.fuel} onChange={e => setCar(p => ({ ...p, fuel: e.target.value }))} options={FUEL_OPTIONS} />
                <FieldInput label="Motor (cm³)" value={car.engine} onChange={e => setCar(p => ({ ...p, engine: e.target.value }))} placeholder="pl. 2993" />
                <FieldInput label="Teljesítmény (LE)" value={car.power} onChange={e => setCar(p => ({ ...p, power: e.target.value }))} placeholder="pl. 265" />
                <FieldSelect label="Váltó" value={car.transmission} onChange={e => setCar(p => ({ ...p, transmission: e.target.value }))} options={TRANSMISSION_OPTIONS} />
                <FieldSelect label="Hajtás" value={car.drive} onChange={e => setCar(p => ({ ...p, drive: e.target.value }))} options={DRIVE_OPTIONS} />
                <FieldSelect label="Karosszéria" value={car.body_type} onChange={e => setCar(p => ({ ...p, body_type: e.target.value }))} options={BODY_OPTIONS} />
                <FieldInput label="Szín" value={car.color} onChange={e => setCar(p => ({ ...p, color: e.target.value }))} placeholder="pl. Fekete" />
              </div>
              <div className="mt-3">
                <FieldInput label="Alvázszám (VIN)" value={car.vin} onChange={e => setCar(p => ({ ...p, vin: e.target.value }))} placeholder="WBAXXXXXXXXXXXXXX" />
              </div>
            </div>

            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-sm mb-4 text-[#D4AF37]">Leírás és felszereltség</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-white/50 mb-1">Felszereltség (soronként egy elem)</label>
                  <textarea value={car.equipment} onChange={e => setCar(p => ({ ...p, equipment: e.target.value }))} rows={4}
                    className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none resize-none"
                    placeholder={"Bőr ülések\nNavigáció\nTolatókamera"} />
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1">Részletes leírás</label>
                  <textarea value={car.description} onChange={e => setCar(p => ({ ...p, description: e.target.value }))} rows={4}
                    className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none resize-none"
                    placeholder="Írja le az autó állapotát, történetét..." />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Origin */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-origin">
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-sm mb-4 text-[#D4AF37]">Jármű származása</h3>
              <div className="grid grid-cols-2 gap-3">
                <FieldInput label="Helyszín (irányítószám + ország)" value={origin.location} onChange={e => setOrigin(p => ({ ...p, location: e.target.value }))} placeholder="pl. AT 89xx" />
                <FieldSelect label="Forrás" value={origin.source_type} onChange={e => setOrigin(p => ({ ...p, source_type: e.target.value }))} options={SOURCE_OPTIONS} />
                <FieldSelect label="Utolsó regisztráció országa" value={origin.last_registration_country} onChange={e => setOrigin(p => ({ ...p, last_registration_country: e.target.value }))} options={COUNTRY_OPTIONS} />
                <div className="flex items-center gap-3 py-2">
                  <label className="text-xs text-white/50">Import jármű?</label>
                  <button type="button" onClick={() => setOrigin(p => ({ ...p, imported: !p.imported }))}
                    className={`w-10 h-5 rounded-full transition-colors ${origin.imported ? "bg-[#D4AF37]" : "bg-white/10"}`}>
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform ${origin.imported ? "translate-x-5" : "translate-x-0.5"}`} />
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-sm mb-4 text-[#D4AF37]">Műszaki vizsgálat</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <FieldInput label="Műszaki vizsga érvényes" value={inspection.technical_inspection_valid_until} onChange={e => setInspection(p => ({ ...p, technical_inspection_valid_until: e.target.value }))} placeholder="pl. 12/2025" />
                <FieldInput label="Utolsó szerviz (km)" value={inspection.last_service_km} onChange={e => setInspection(p => ({ ...p, last_service_km: e.target.value }))} placeholder="pl. 189000" />
                <FieldSelect label="Szervizkönyv" value={inspection.service_book_status} onChange={e => setInspection(p => ({ ...p, service_book_status: e.target.value }))} options={SERVICE_BOOK_OPTIONS} />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: History + Tires + Paint */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-technical">
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-sm mb-4 text-[#D4AF37]">Jármű története</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <FieldInput label="Első forgalomba helyezés" value={history.first_registration} onChange={e => setHistory(p => ({ ...p, first_registration: e.target.value }))} placeholder="pl. 03/2019" />
                <FieldInput label="Előző tulajdonosok száma" type="number" value={history.previous_owners} onChange={e => setHistory(p => ({ ...p, previous_owners: e.target.value }))} placeholder="pl. 2" />
                <FieldInput label="Kulcsok száma" type="number" value={history.key_count} onChange={e => setHistory(p => ({ ...p, key_count: e.target.value }))} placeholder="pl. 2" />
                <FieldSelect label="Rendhagyó használat" value={history.unusual_usage} onChange={e => setHistory(p => ({ ...p, unusual_usage: e.target.value }))} options={USAGE_OPTIONS} />
              </div>
            </div>

            {/* Tires section */}
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5" data-testid="tires-section">
              <h3 className="font-semibold text-sm mb-4 text-[#D4AF37]">Gumiabroncsok</h3>
              <div className="flex flex-wrap gap-3 mb-4">
                <Toggle checked={tires.summer.enabled} onChange={v => setTires(p => ({ ...p, summer: { ...p.summer, enabled: v } }))} label="Nyári gumi" />
                <Toggle checked={tires.winter.enabled} onChange={v => setTires(p => ({ ...p, winter: { ...p.winter, enabled: v } }))} label="Téli gumi" />
              </div>
              <div className="space-y-3">
                {tires.summer.enabled && (
                  <TireSetFields label="Nyári gumik" icon={Sun} data={tires.summer}
                    onChange={d => setTires(p => ({ ...p, summer: { ...d, enabled: true } }))} />
                )}
                {tires.winter.enabled && (
                  <TireSetFields label="Téli gumik" icon={Snowflake} data={tires.winter}
                    onChange={d => setTires(p => ({ ...p, winter: { ...d, enabled: true } }))} />
                )}
                {!tires.summer.enabled && !tires.winter.enabled && (
                  <p className="text-xs text-white/30 py-2">Válasszon gumitípust a részletek megadásához.</p>
                )}
              </div>
            </div>

            {/* Paint / Damage section */}
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5" data-testid="damage-section">
              <h3 className="font-semibold text-sm mb-4 text-[#D4AF37]">Fényezés / Sérülés</h3>
              <div className="mb-4">
                <Toggle checked={damage.has_damage} onChange={v => setDamage(p => ({ ...p, has_damage: v }))} label="Volt-e sérülés az autón?" />
              </div>

              {damage.has_damage && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Sérülés leírása</label>
                    <textarea value={damage.description} onChange={e => setDamage(p => ({ ...p, description: e.target.value }))} rows={3}
                      className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none resize-none"
                      placeholder="Írja le a sérülés típusát és helyét..." />
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-2">Jelölje meg az érintett paneleket:</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {PAINT_PANELS.map(({ key, label }) => {
                        const selected = damage.damaged_panels.includes(key);
                        return (
                          <button key={key} type="button" onClick={() => toggleDamagedPanel(key)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs border transition-colors ${
                              selected
                                ? "border-red-500/40 bg-red-500/10 text-red-400"
                                : "border-white/10 bg-white/[0.02] text-white/60 hover:bg-white/5"
                            }`}
                            data-testid={`panel-${key}`}>
                            {selected ? <AlertTriangle className="w-3 h-3" /> : <div className="w-3 h-3 rounded-sm border border-white/20" />}
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Damage photos upload (inline) */}
                  <div className="border-t border-white/5 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs text-white/60">Sérülés fotók</label>
                      <span className="text-[10px] text-white/40">{(imagesByCategory.damage || []).length} kép</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setUploadCategory("damage"); fileInputRef.current?.click(); }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-white/5 hover:bg-white/10 border border-dashed border-white/15 hover:border-[#D4AF37]/40 rounded-lg text-xs text-white/70 transition-colors"
                      data-testid="upload-damage-inline"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      Sérülés fotók feltöltése
                    </button>
                    {(imagesByCategory.damage || []).length > 0 && (
                      <div className="flex gap-2 flex-wrap mt-3" data-testid="damage-photos-preview">
                        {(imagesByCategory.damage || []).map((img, i) => (
                          <div key={`${img.preview}-${i}`} className="relative w-16 h-12 rounded-md overflow-hidden group">
                            <img src={img.preview} alt="" className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removeImage("damage", i)}
                              className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                              data-testid={`remove-damage-${i}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-white/35 mt-2">Tipp: tölts fel közeli képet minden bejelölt panelről, hogy a vevő pontosan lássa a sérülést.</p>
                  </div>
                </div>
              )}

              {!damage.has_damage && (
                <p className="text-xs text-white/40">
                  A fényezésvastagság értékek automatikusan generálódnak gyári tartományban (90-150 µm).
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Images + Documents */}
        {step === 3 && (
          <div className="space-y-4" data-testid="step-images">
            {/* Car images */}
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-sm text-[#D4AF37]">Képek feltöltése</h3>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${imageOnlyTotal >= 20 ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                  {imageOnlyTotal} / 20 kép
                </span>
              </div>

              <div className="space-y-3">
                {IMAGE_CATEGORIES.map(cat => {
                  const imgs = imagesByCategory[cat.id] || [];
                  const met = imgs.length >= cat.min;
                  return (
                    <div key={cat.id} className="border border-white/5 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {met ? <Check className="w-4 h-4 text-green-400" /> : <Camera className="w-4 h-4 text-white/30" />}
                          <span className="text-sm text-white">{cat.label}</span>
                          <span className="text-xs text-white/30">(min: {cat.min})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs ${met ? "text-green-400" : "text-white/40"}`}>{imgs.length} kép</span>
                          <button type="button" onClick={() => { setUploadCategory(cat.id); fileInputRef.current?.click(); }}
                            className="px-2.5 py-1 text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-md transition-colors"
                            data-testid={`upload-${cat.id}`}>
                            <Upload className="w-3 h-3 inline mr-1" />Feltöltés
                          </button>
                        </div>
                      </div>
                      {imgs.length > 0 && (
                        <div className="flex gap-2 flex-wrap mt-2">
                          {imgs.map((img, i) => (
                            <div key={i} className="relative w-16 h-12 rounded-md overflow-hidden group">
                              <img src={img.preview} alt="" className="w-full h-full object-cover" />
                              <button onClick={() => removeImage(cat.id, i)}
                                className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                <X className="w-4 h-4 text-red-400" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Documents */}
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-4 h-4 text-[#D4AF37]" />
                <h3 className="font-semibold text-sm text-[#D4AF37]">Dokumentumok feltöltése</h3>
              </div>
              <p className="text-xs text-white/40 mb-3">Töltse fel a jármű számláit, bizonylatait és forgalmi engedélyét (fotó vagy szkennelt dokumentum).</p>
              <div className="space-y-3">
                {DOC_CATEGORIES.map(cat => {
                  const imgs = imagesByCategory[cat.id] || [];
                  return (
                    <div key={cat.id} className="border border-white/5 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-white/30" />
                          <span className="text-sm text-white">{cat.label}</span>
                          <span className="text-xs text-white/30">(opcionális)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-white/40">{imgs.length} fájl</span>
                          <button type="button" onClick={() => { setUploadCategory(cat.id); fileInputRef.current?.click(); }}
                            className="px-2.5 py-1 text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-md transition-colors"
                            data-testid={`upload-${cat.id}`}>
                            <Upload className="w-3 h-3 inline mr-1" />Feltöltés
                          </button>
                        </div>
                      </div>
                      {imgs.length > 0 && (
                        <div className="flex gap-2 flex-wrap mt-2">
                          {imgs.map((img, i) => (
                            <div key={i} className="relative w-16 h-12 rounded-md overflow-hidden group">
                              <img src={img.preview} alt="" className="w-full h-full object-cover" />
                              <button onClick={() => removeImage(cat.id, i)}
                                className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                <X className="w-4 h-4 text-red-400" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Hidden file input - always mounted so any step can trigger upload */}
        <input ref={fileInputRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={handleImageUpload} />

        {/* Step 4: Preview */}
        {step === 4 && (
          <div className="space-y-6" data-testid="step-preview">
            <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl p-4 text-center">
              <Eye className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
              <p className="text-sm text-[#D4AF37] font-medium">Előnézet - Ellenőrizze az adatokat beküldés előtt</p>
            </div>

            {/* Image preview */}
            {allImages.length > 0 && (
              <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
                <h3 className="font-semibold text-sm text-[#D4AF37] mb-3">Képek ({imageOnlyTotal}) {DOC_CATEGORIES.reduce((s, c) => s + (imagesByCategory[c.id] || []).length, 0) > 0 && `+ Dokumentumok`}</h3>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {allImages.map((img, i) => (
                    <img key={i} src={img.preview} alt="" className="w-full aspect-[4/3] rounded-lg object-cover" />
                  ))}
                </div>
              </div>
            )}

            {/* Car data */}
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-sm text-[#D4AF37] mb-3">Jármű adatok</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                {[
                  ["Márka", car.brand], ["Modell", car.model], ["Évjárat", car.year],
                  ["Ár", `${car.price} €`], ["Kilométer", car.mileage], ["Üzemanyag", car.fuel],
                  ["Motor", `${car.engine} cm³`], ["Teljesítmény", `${car.power} LE`], ["Váltó", car.transmission],
                  ["Hajtás", car.drive], ["Karosszéria", car.body_type], ["Szín", car.color],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between items-center py-1 border-b border-white/5">
                    <span className="text-white/40 text-xs">{k}</span>
                    <span className="text-white font-medium text-xs">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-white/5">
                <span className="text-xs text-white/40">VIN:</span>
                <span className="ml-2 text-xs font-mono text-white">{car.vin}</span>
              </div>
            </div>

            {/* Origin & Inspection */}
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-sm text-[#D4AF37] mb-3">Származás és műszaki</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ["Helyszín", origin.location], ["Forrás", origin.source_type],
                  ["Regisztráció országa", origin.last_registration_country],
                  ["Import", origin.imported ? "Igen" : "Nem"],
                  ["Műszaki vizsga", inspection.technical_inspection_valid_until],
                  ["Utolsó szerviz", `${inspection.last_service_km} km`], ["Szervizkönyv", inspection.service_book_status],
                  ["Első forgalomba helyezés", history.first_registration], ["Előző tulajdonosok", history.previous_owners],
                  ["Kulcsok", history.key_count], ["Rendhagyó használat", history.unusual_usage],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between items-center py-1 border-b border-white/5">
                    <span className="text-white/40 text-xs">{k}</span>
                    <span className="text-white text-xs">{v || "-"}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tires preview */}
            {(tires.summer.enabled || tires.winter.enabled) && (
              <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
                <h3 className="font-semibold text-sm text-[#D4AF37] mb-3">Gumiabroncsok</h3>
                <div className="space-y-2 text-sm">
                  {tires.summer.enabled && (
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4 text-yellow-400" />
                      <span className="text-white">Nyári: {tires.summer.brand} {tires.summer.size}</span>
                      {tires.summer.tread_depth && <span className="text-white/40 text-xs">({tires.summer.tread_depth} mm)</span>}
                    </div>
                  )}
                  {tires.winter.enabled && (
                    <div className="flex items-center gap-2">
                      <Snowflake className="w-4 h-4 text-blue-400" />
                      <span className="text-white">Téli: {tires.winter.brand} {tires.winter.size}</span>
                      {tires.winter.tread_depth && <span className="text-white/40 text-xs">({tires.winter.tread_depth} mm)</span>}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Damage preview */}
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-sm text-[#D4AF37] mb-3">Fényezés / Sérülés</h3>
              {damage.has_damage ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-yellow-400 text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    <span>Sérülés jelölve</span>
                  </div>
                  {damage.description && <p className="text-xs text-white/60">{damage.description}</p>}
                  {damage.damaged_panels.length > 0 && (
                    <p className="text-xs text-red-400/80">
                      Érintett: {damage.damaged_panels.map(k => PAINT_PANELS.find(p => p.key === k)?.label).join(", ")}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-green-400/80">Nincs sérülés jelölve - gyári fényezés</p>
              )}
            </div>

            {car.description && (
              <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
                <h3 className="font-semibold text-sm text-[#D4AF37] mb-2">Leírás</h3>
                <p className="text-sm text-white/70 whitespace-pre-wrap">{car.description}</p>
              </div>
            )}

            {/* Submitter */}
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5">
              <h3 className="font-semibold text-sm text-[#D4AF37] mb-3">Feltöltő adatai</h3>
              <div className="text-sm space-y-1">
                <p className="text-white">{submitter.name}</p>
                <p className="text-white/60">{submitter.email} | {submitter.phone}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/5">
          {step > 0 ? (
            <Button onClick={prevStep} variant="outline" className="border-white/10 text-white hover:bg-white/5">
              <ChevronLeft className="w-4 h-4 mr-1" /> Vissza
            </Button>
          ) : <div />}

          {step < 4 ? (
            <Button onClick={nextStep} className="bg-[#D4AF37] text-black hover:bg-[#c4a030]" data-testid="next-step-btn">
              Tovább <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting}
              className="bg-green-600 hover:bg-green-700 text-white px-8" data-testid="submit-car-btn">
              {submitting ? "Beküldés folyamatban..." : "Hirdetés beküldése"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
